/**
 * Typed wrapper around @auth0/auth0-react's useAuth0().
 *
 * Centralizes our app-specific assumptions:
 *   - `tenant_id` and `role` arrive as custom claims (added by an Auth0 Action
 *     on the Login flow — see docs/auth0-setup.md §4).
 *   - We surface them as flat fields on the user.
 *
 * Sprint 0: the wrapper is intentionally minimal. Sprint 1+ extends with
 * permission helpers (`can(permission: string): boolean`) once RBAC lands.
 */
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo } from 'react';

const NS = 'https://yhelp.ysec.com.br/claims';

export interface YhelpUser {
  sub: string;
  email: string;
  name: string;
  picture?: string;
  tenantId?: string;
  role?: string;
}

export interface UseAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: YhelpUser | null;
  loginWithRedirect: ReturnType<typeof useAuth0>['loginWithRedirect'];
  logout: ReturnType<typeof useAuth0>['logout'];
  getAccessTokenSilently: ReturnType<typeof useAuth0>['getAccessTokenSilently'];
}

export function useAuth(): UseAuthResult {
  const auth0 = useAuth0();

  const user = useMemo<YhelpUser | null>(() => {
    if (!auth0.user) return null;
    const claims = auth0.user as Record<string, unknown>;
    return {
      sub: String(claims.sub ?? ''),
      email: String(claims.email ?? ''),
      name: String(claims.name ?? ''),
      picture: claims.picture as string | undefined,
      tenantId: claims[`${NS}/tenant_id`] as string | undefined,
      role: claims[`${NS}/role`] as string | undefined,
    };
  }, [auth0.user]);

  return {
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    user,
    loginWithRedirect: auth0.loginWithRedirect,
    logout: auth0.logout,
    getAccessTokenSilently: auth0.getAccessTokenSilently,
  };
}
